// Import research mode types for use in this file
import type { ResearchMode } from './researchModes';

// User/Customer types
export interface Customer {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_master_user: boolean;
  email_verified: boolean;
  subscription_status: string;
  trial_days_remaining: number;
  files_count: number;
  max_files: number | null;  // null means unlimited (for master users)
  created_at?: string;
  // Promotional/Founder account fields
  is_promotional_account: boolean;
  promotional_days_remaining: number;
  is_founder: boolean;  // True for influencer (Founder) accounts
  has_completed_onboarding: boolean;  // Whether user has completed the product tour
}

// Auth types
export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  fingerprint_id?: string;  // For A/B test tracking
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface EmailVerificationStatusResponse {
  email_verified: boolean;
  email: string;
  message?: string;
}

// Subscription types
export interface SubscriptionStatus {
  status: string;
  plan_type?: string;
  trial_start_date?: string;
  trial_end_date?: string;
  trial_days_remaining: number;
  is_active: boolean;
}

// Preferences types
export interface Preferences {
  id: string;
  preset_id?: string;
  custom_prompt?: string;
  delivery_time: string;
  timezone: string;
  timezone_abbr?: string;  // Short timezone name (PST, EST, etc.)
  is_active: boolean;
  preset_name?: string;
  prompt_template?: string;  // Full prompt template from preset or custom prompt
  next_newsletter_date?: string;  // Next scheduled newsletter date (YYYY-MM-DD)
  next_newsletter_time?: string;  // Next scheduled newsletter time (HH:MM)
  created_at: string;
  updated_at: string;
}

export interface PreferencesRequest {
  preset_id?: string;
  custom_prompt?: string;
  delivery_time: string;
  timezone: string;
  is_active: boolean;
}

// Delivery Schedule types (for multiple newsletters per day)
export interface DeliverySchedule {
  id: string;
  delivery_time: string;  // HH:MM format
  timezone: string;
  timezone_abbr?: string;
  label?: string;  // e.g., "Morning", "Evening"
  is_active: boolean;
  schedule_order: number;
  created_at: string;
  updated_at: string;
}

export interface DeliveryScheduleRequest {
  delivery_time: string;
  timezone: string;
  label?: string;
  is_active?: boolean;
}

export interface DeliverySchedulesResponse {
  schedules: DeliverySchedule[];
  max_schedules: number;  // Maximum allowed for user's tier
  newsletters_per_day: number;  // Current tier's limit
  can_add_more: boolean;  // Whether user can add more schedules
}

// Newsletter types
export interface Newsletter {
  id: string;
  topic?: string;
  subject?: string;
  content?: string;
  content_summary?: string;
  s3_url?: string;
  generated_at?: string;
  sent_at?: string;
  status: string;
  created_at: string;
  started_at?: string;  // When generation started
  completed_at?: string;  // When generation completed
  duration_seconds?: number;  // Total generation duration in seconds
}

export interface NewsletterHistory {
  id: string;
  subject: string;
  content_summary: string;
  s3_url?: string;
  generated_at: string;
  sent_at?: string;
  status: string;
  started_at?: string;  // When generation started
  completed_at?: string;  // When generation completed
  duration_seconds?: number;  // Total generation duration in seconds
}

// S3 Report types (for master users)
export interface S3Report {
  key: string;
  url: string;
  last_modified: string;
  size: number;
}

export interface S3ReportsResponse {
  reports: S3Report[];
  customer_folder?: string;
  bucket?: string;
  error?: string;
}

export interface GenerateNewsletterRequest {
  topic?: string;
  custom_prompt?: string;
  agent_count?: number;
  enable_web_research?: boolean;
  send_email?: boolean;
}

export interface CanGenerateResponse {
  can_generate: boolean;
  reason?: string;
  sent_today: number;
  max_per_day: number;
  remaining: number;
}

// File types
export interface UploadedFile {
  id: string;
  filename: string;
  original_filename: string;
  file_type: string;
  status: string;
  uploaded_at: string;
  session_id?: string | null;
  is_session_file?: boolean;
  expires_at?: string | null;
  processing_status?: string | null;  // pending, processing, completed, failed
}

// Preset types
export interface Preset {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  prompt_template: string;
}

export interface PresetsResponse {
  presets: Preset[];
  categories: string[];
  total: number;
}

// Tier types
export interface TierLimits {
  newsletters_per_day: number;
  max_file_uploads?: number;
  agents_per_task: number;
  web_sources_per_agent: number;
}

export interface TierPricing {
  monthly: number;
  annual: number;
  annual_monthly: number;
  discount_percent: number;
  currency: string;
  stripe_price_id_monthly?: string;
  stripe_price_id_annual?: string;
}

export interface Tier {
  tier: string;
  description: string;
  tagline: string;
  features: string[];
  limits: TierLimits;
  persistent_storage: boolean;
  enabled: boolean;
  pricing?: TierPricing;
}

export interface TiersResponse {
  beta_phase: boolean;
  beta_message?: string;
  tiers: Tier[];
}

// Model types (for verdict/council components)
export interface ModelInfo {
  name: string;  // Display name (e.g., "GPT-4o", "Claude Opus 4")
  full_name: string;  // Full LiteLLM name (e.g., "openai/gpt-4o")
  provider: string;  // Provider name (e.g., "openai", "anthropic", "google", "deepseek")
  tier: string;  // Cost tier: "budget", "standard", "premium"
  description?: string;
}

export interface ModelsListResponse {
  models: ModelInfo[];
  by_tier: {
    budget: ModelInfo[];
    standard: ModelInfo[];
    premium: ModelInfo[];
  };
  total_count: number;
  providers: string[];
}

// Model tier display types (for marketing/UI)
export interface TierDisplayInfo {
  name: string;  // Marketing name (e.g., "Economy", "Professional", "Enterprise")
  badge_color: string;  // UI badge color (e.g., "gray", "blue", "gold")
  description: string;  // Short description
  marketing: string;  // Marketing copy for the tier
  icon: string;  // Icon name (e.g., "zap", "briefcase", "crown")
}

export interface ModelTierInfoResponse {
  tier_info: {
    budget: TierDisplayInfo;
    standard: TierDisplayInfo;
    premium: TierDisplayInfo;
  };
  current_tier: 'budget' | 'standard' | 'premium';
  is_authenticated: boolean;
  can_change_tier: boolean;
  free_credits_on_signup: number;
  cta_message?: string;
}

// Feedback types
export type FeedbackType =
  | 'general'
  | 'newsletter'
  | 'feature_request'
  | 'bug_report'
  | 'billing'
  | 'ui'
  | 'suggestion'
  | 'other';

export interface FeedbackRequest {
  newsletter_id?: string;
  rating?: number;
  comment?: string;
  feedback_text?: string;
  feedback_type?: FeedbackType;
  newsletter_history_id?: string;
  subject?: string;
}

export interface Feedback {
  id: string;
  rating?: number;
  comment?: string;
  feedback_text?: string;
  feedback_type?: FeedbackType;
  newsletter_history_id?: string;
  subject?: string;
  created_at: string;
}

// Contact form types (public, no auth required)
export interface ContactFormRequest {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export interface ContactFormResponse {
  success: boolean;
  message: string;
}

// Task status types
export interface TaskProgress {
  status: string;
  step: number;
  total_steps: number;
  stage?: string;  // e.g., 'research', 'writing', 'fact_checking'
  stage_label?: string;  // Human readable: "Researching topics..."
  elapsed_seconds?: number;
  estimated_remaining_seconds?: number;
}

export interface TaskStatus {
  task_id: string;
  status: string;  // PENDING, STARTED, PROGRESS, SUCCESS, FAILURE, RETRY
  ready: boolean;
  progress?: TaskProgress;
  result?: Record<string, unknown>;
  error?: string;
}

// Active task tracking - in-progress newsletters for a customer
export interface ActiveTask {
  task_id: string;
  history_id: string;
  topic?: string;
  status: string;
  started_at: string;
  content_summary?: string;
  elapsed_minutes?: number;
  progress?: TaskProgress;
  // Detailed progress fields (matching admin view)
  custom_prompt?: string | null;
  timezone?: string | null;
  current_phase?: string | null;
  current_task_name?: string | null;
  total_tasks?: number;
  current_task_index?: number;
}

// Recent issues/errors during generation (for admin users)
export interface RecentIssue {
  id: string;
  timestamp: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  category: string;
  error_type: string;
  error_message: string | null;
  task_id?: string;
}

export interface ActiveTasksResponse {
  tasks: ActiveTask[];
  has_active: boolean;
  /** Recent errors/warnings during generation (admin only) */
  recent_issues?: RecentIssue[];
  /** Count of recent issues (admin only) */
  issue_count?: number;
}

// API Error
export interface ApiError {
  detail: string;
  error_id?: string;
  timestamp?: string;
}

// Admin Monitoring Types
export type ErrorSeverity = 'debug' | 'info' | 'warning' | 'error' | 'critical';
export type ErrorCategory =
  | 'task_execution'
  | 'database'
  | 'api'
  | 'authentication'
  | 'external_service'
  | 'ai_model'
  | 'email'
  | 'file_processing'
  | 'webhook'
  | 'system';

export interface AdminErrorLog {
  id: string;
  timestamp: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  error_type: string;
  error_message: string;
  customer_id?: string;
  task_id?: string;
  resolved: boolean;
}

export interface AdminErrorDetail extends AdminErrorLog {
  stack_trace?: string;
  context?: Record<string, unknown>;
  run_id?: string;
  resolution_notes?: string;
}

export interface AdminErrorsResponse {
  count: number;
  errors: AdminErrorLog[];
}

export interface AdminErrorSummary {
  total: number;
  unresolved: number;
  by_severity: Record<string, number>;
  by_category: Record<string, number>;
  recent_critical: number;
}

// Active generation monitoring types
export interface ActiveGeneration {
  history_id: string;
  customer_id: string;
  customer_email: string;
  customer_name: string;
  customer_timezone: string | null;
  subject: string;
  status: 'queued' | 'generating';
  started_at: string | null;
  task_id: string | null;
  elapsed_minutes: number | null;
  preset_id: string | null;
  custom_prompt: string | null;
  current_phase: string | null;
  current_task_name: string | null;
  total_tasks: number;
  current_task_index: number;
}

export interface ActiveGenerationsResponse {
  count: number;
  generations: ActiveGeneration[];
}

// User spending stats types (Admin)
export interface UserSpendingStats {
  id: string;
  customer_id: string;
  email: string;
  customer_name: string;
  user_tier: string;
  is_master_user: boolean;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost: number;
  total_api_calls: number;
  total_newsletters_generated: number;
  last_api_call_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserSpendingTotals {
  total_cost: number;
  total_tokens: number;
  total_newsletters: number;
}

export interface UserSpendingResponse {
  count: number;
  users: UserSpendingStats[];
  totals: UserSpendingTotals;
}

// Recent generations types (Admin)
export interface RecentGeneration {
  history_id: string;
  customer_id: string;
  customer_email: string;
  customer_name: string;
  subject: string;
  status: string;
  generated_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  task_id: string | null;
  preset_id: string | null;
  customer_timezone: string;
  total_cost: number | null;
  total_tasks: number;
  s3_url: string | null;
  content_summary: string | null;
}

export interface RecentGenerationsResponse {
  count: number;
  total: number;
  offset: number;
  limit: number;
  generations: RecentGeneration[];
}

// Detailed generation info (Admin)
export interface CostSummary {
  total_cost: number;
  total_input_tokens: number;
  total_output_tokens: number;
  by_model?: Record<string, {
    cost: number;
    input_tokens: number;
    output_tokens: number;
  }>;
}

export interface GenerationDetail {
  history_id: string;
  customer_id: string;
  customer_email: string;
  customer_name: string;
  subject: string;
  status: string;
  generated_at: string | null;
  completed_at: string | null;
  started_at: string | null;
  sent_at: string | null;
  duration_seconds: number | null;
  task_id: string | null;
  preset_id: string | null;
  customer_timezone: string;
  s3_url: string | null;
  content_summary: string | null;
  custom_prompt: string | null;
  cost_summary: CostSummary | null;
  sub_tasks: SubTask[];
  total_tasks: number;
  current_phase: string | null;
  error_message: string | null;
}

// Analytics types (Admin)
export interface DailyAnalytics {
  date: string;
  total_generations: number;
  successful_generations: number;
  failed_generations: number;
  avg_duration_seconds: number;
  total_duration_seconds: number;
  total_cost: number;
  total_input_tokens: number;
  total_output_tokens: number;
}

export interface AnalyticsSummary {
  total_generations: number;
  total_successful: number;
  total_failed: number;
  total_cost: number;
  avg_cost_per_generation: number;
  avg_duration_seconds: number;
}

export interface AnalyticsResponse {
  daily_data: DailyAnalytics[];
  summary: AnalyticsSummary;
}

// Comparison types (Admin)
export interface ComparisonResponse {
  count: number;
  generations: GenerationDetail[];
}

// Free tier report types (Admin)
export interface FreeReport {
  id: string;
  lead_id: string;
  topic: string;
  report_type: string; // 'initial', 'followup_24h', 'followup_3d'
  tier_used: string; // 'basic', 'standard'
  status: string; // 'queued', 'generating', 'sent', 'failed'
  history_id: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  created_at: string | null;
  email: string;
  reports_used: number;
  converted_to_customer: boolean;
}

export interface FreeReportsResponse {
  count: number;
  reports: FreeReport[];
}

// =============================================================================
// Verdict Admin Monitoring Types
// =============================================================================

/** Fingerprint statistics (landing page - anonymous users) */
export interface VerdictFingerprintStats {
  total_fingerprints: number;
  total_queries: number;
  single_query_users: number;
  multi_query_users: number;
  converted_to_lead: number;
  avg_queries_per_user: number;
}

/** Lead statistics (email-captured users) */
export interface VerdictLeadStats {
  total_leads: number;
  total_reports: number;
  converted_leads: number;
  reports_sent: number;
  reports_failed: number;
  reports_generating: number;
}

/** Session statistics (dashboard - authenticated users) */
export interface VerdictSessionStats {
  total_sessions: number;
  active_sessions: number;
  archived_sessions: number;
  unique_users: number;
}

/** Daily breakdown item for fingerprints */
export interface VerdictDailyFingerprint {
  date: string;
  new_users: number;
  queries: number;
}

/** Daily breakdown item for leads */
export interface VerdictDailyLead {
  date: string;
  new_leads: number;
}

/** Complete Verdict admin stats response */
export interface VerdictAdminStatsResponse {
  date_range: {
    start: string | null;
    end: string | null;
  };
  fingerprint_stats: VerdictFingerprintStats;
  lead_stats: VerdictLeadStats;
  session_stats: VerdictSessionStats;
  daily_breakdown: {
    fingerprints: VerdictDailyFingerprint[];
    leads: VerdictDailyLead[];
  };
}

/** Active session (dashboard user) */
export interface VerdictActiveSession {
  session_id: string;
  title: string;
  customer_id: string;
  customer_email: string;
  customer_name: string;
  created_at: string | null;
  updated_at: string | null;
  message_count: number;
  last_message_at: string | null;
  source: 'dashboard';
}

/** Active fingerprint (landing page user) */
export interface VerdictActiveFingerprint {
  fingerprint_id: string;
  query_count: number;
  last_topic: string | null;
  last_query_at: string | null;
  converted_to_lead: boolean;
  lead_id: string | null;
  source: 'landing_page';
}

/** Generating report (free tier) */
export interface VerdictGeneratingReport {
  report_id: string;
  topic: string;
  status: string;
  created_at: string | null;
  email: string;
  tier_used: string;
  source: 'free_tier';
}

/** Active logic trees response */
export interface VerdictActiveLogicTreesResponse {
  active_sessions: VerdictActiveSession[];
  active_fingerprints: VerdictActiveFingerprint[];
  generating_reports: VerdictGeneratingReport[];
  totals: {
    dashboard_sessions: number;
    landing_page_users: number;
    generating_reports: number;
  };
}

/** Fingerprint list item */
export interface VerdictFingerprintItem {
  fingerprint_id: string;
  query_count: number;
  first_query_at: string | null;
  last_query_at: string | null;
  first_topic: string | null;
  last_topic: string | null;
  converted_to_lead: boolean;
  lead_id: string | null;
  ip_address: string | null;
  created_at: string | null;
}

/** Fingerprints list response */
export interface VerdictFingerprintsResponse {
  fingerprints: VerdictFingerprintItem[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

/** Research session list item (admin view) */
export interface VerdictResearchSessionItem {
  session_id: string;
  title: string;
  status: string;
  customer_id: string;
  customer_email: string;
  customer_name: string;
  created_at: string | null;
  updated_at: string | null;
  message_count: number;
}

/** Research sessions list response */
export interface VerdictResearchSessionsResponse {
  sessions: VerdictResearchSessionItem[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

/** Cost tracking user stats */
export interface VerdictUserCostStats {
  customer_id: string;
  email: string;
  name: string;
  user_tier: string;
  is_master_user: boolean;
  credit_balance: number | null;
  estimated_credits_used: number | null;
  credit_usage_percentage: number | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost: number;
  total_api_calls: number;
  last_api_call_at: string | null;
  created_at: string | null;
}

/** Cost tracking summary */
export interface VerdictCostSummary {
  total_platform_cost: number;
  total_platform_tokens: number;
  free_tier_estimated_cost: number;
  fingerprint_estimated_cost: number;
  total_estimated_cost: number;
  active_paid_users: number;
  total_users_tracked: number;
}

/** Free tier cost stats */
export interface VerdictFreeTierCostStats {
  total_report_queries: number;
  basic_tier_queries: number;
  standard_tier_queries: number;
  fingerprint_queries: number;
  estimated_cost: number;
}

/** Credit transaction summary */
export interface VerdictCreditTransactionSummary {
  total_credits: number;
  count: number;
}

/** Daily cost data */
export interface VerdictDailyCost {
  date: string;
  credits_used: number;
  estimated_cost: number;
  transactions: number;
}

/** Cost tracking response */
export interface VerdictCostTrackingResponse {
  date_range: {
    start: string | null;
    end: string | null;
  };
  summary: VerdictCostSummary;
  free_tier_stats: VerdictFreeTierCostStats;
  credit_transactions: Record<string, VerdictCreditTransactionSummary>;
  daily_costs: VerdictDailyCost[];
  users: VerdictUserCostStats[];
}

// Web content cache types (Admin)
export interface CacheStats {
  hits: number;
  misses: number;
  hit_rate: number;
  total_entries: number;
}

export interface CacheStatsResponse {
  url_cache: CacheStats;
  query_cache: CacheStats;
  savings: {
    estimated_credits_saved: number;
    estimated_cost_saved: number;
  };
}

export interface CacheCleanupResponse {
  status: string;
  urls_deleted: number;
  queries_deleted: number;
}

// Stripe types
export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface BillingPortalResponse {
  portal_url: string;
}

export interface StripeConfig {
  publishable_key: string;
}

// =============================================================================
// Real-Time Progress Tracking Types (for SSE)
// =============================================================================

/** Status of an individual step within a task */
export type TaskStepStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped';

/** Type of step in the generation pipeline */
export type TaskStepType = 'agent_research' | 'aggregation' | 'fact_checking' | 'debate';

/** Individual step within a sub-task */
export interface TaskStep {
  step_id: string;
  step_type: TaskStepType;
  name: string;
  status: TaskStepStatus;
  started_at?: string;
  completed_at?: string;
  details?: string;
  metadata?: Record<string, unknown>;
}

/** A sub-task in the generation pipeline (one research topic/aspect) */
export interface SubTask {
  task_id: string;
  task_index: number;
  name: string;
  description: string;
  status: TaskStepStatus;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  cost?: number;
  agent_count: number;
  steps: TaskStep[];
  confidence_score?: number;
}

/** Overall generation progress state */
export interface GenerationProgress {
  history_id: string;
  status: 'queued' | 'generating' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
  current_phase: string;
  current_task_index: number;
  total_tasks: number;
  sub_tasks: SubTask[];
  error_message?: string;
}

/** SSE event types */
export type ProgressEventType = 'progress' | 'complete' | 'failed' | 'heartbeat' | 'error' | 'timeout';

/** SSE event data */
export interface ProgressEvent {
  type: ProgressEventType;
  data: GenerationProgress | { status: string; history_id?: string; error?: string; timestamp?: string };
}

// =============================================================================
// Research History Types (pgvector-based semantic search)
// =============================================================================

/** Individual research history entry */
export interface ResearchHistoryEntry {
  id: number;
  topic: string;
  key_insights: string[];
  entities: Record<string, string[]>;
  sources_used: string[];
  research_date: string;
  confidence_score: number;
  task_count: number;
  newsletter_history_id?: string;
  similarity?: number;  // For similarity search results
}

/** Similar research result with similarity score */
export interface SimilarResearch {
  entry: ResearchHistoryEntry;
  similarity: number;
  days_ago: number;
}

/** Research history list response */
export interface ResearchHistoryResponse {
  entries: ResearchHistoryEntry[];
  total: number;
}

/** Research statistics for a user */
export interface ResearchStats {
  total_entries: number;
  avg_confidence: number;
  avg_tasks_per_research: number;
  first_research_date?: string;
  last_research_date?: string;
  total_research_days: number;
}

/** Topic novelty check result */
export interface TopicNoveltyResponse {
  is_novel: boolean;
  similar_topics: Array<{
    topic: string;
    similarity: number;
    days_ago: number;
  }>;
  recommendation?: string;
}

/** Similar research search response */
export interface SimilarResearchResponse {
  query: string;
  results: Array<{
    entry: ResearchHistoryEntry;
    similarity: number;
    days_ago: number;
  }>;
  count: number;
}

// =============================================================================
// Smart Router Types
// =============================================================================

/** Research intensity levels for smart routing (must match backend ResearchIntensity enum) */
export type ResearchIntensity = 'fast' | 'deep';

/** Task capability types for smart routing */
export type TaskCapability = 'reasoning' | 'coding' | 'math' | 'creative' | 'factual';

/** Intensity configuration for display */
export interface IntensityConfig {
  label: string;
  description: string;
  /** @deprecated No longer shown to users */
  creditMultiplier: string;
  icon: string;
  agentCount: number;
  timeoutSeconds: number;
  webSearches: number;
}

/**
 * Approximate minimum credits needed per intensity level (across all blueprint types).
 * Used as a frontend gate to show InsufficientCreditsDialog before starting research,
 * preventing poor UX of streams starting then failing mid-way on the backend check.
 * Exact cost check still happens server-side during credit reservation.
 */
export const INTENSITY_CREDIT_THRESHOLDS: Record<ResearchIntensity, number> = {
  fast: 5,
  deep: 15,
};

/** Smart routing intensity configurations */
export const INTENSITY_CONFIGS: Record<ResearchIntensity, IntensityConfig> = {
  fast: {
    label: 'Fast',
    description: 'Fast answer from 2 AI models for simple questions',
    creditMultiplier: '',
    icon: 'zap',
    agentCount: 2,
    timeoutSeconds: 60,
    webSearches: 3,
  },
  deep: {
    label: 'Deep',
    description: 'Thorough research with fact-checking and debate',
    creditMultiplier: '',
    icon: 'brain',
    agentCount: 3,
    timeoutSeconds: 90,
    webSearches: 9,
  },
};

/** User tier access to intensity levels - used only for unauthenticated/free users */
export const TIER_INTENSITY_ACCESS: Record<string, ResearchIntensity[]> = {
  free: ['fast'],
  trial: ['fast', 'deep'],
  basic: ['fast', 'deep'],
  standard: ['fast', 'deep'],
  professional: ['fast', 'deep'],
  enterprise: ['fast', 'deep'],
  master: ['fast', 'deep'],
};

/** Capability display configuration */
export interface CapabilityConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
}

/** Capability badge configurations */
export const CAPABILITY_CONFIGS: Record<TaskCapability, CapabilityConfig> = {
  reasoning: {
    label: 'Reasoning',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    icon: 'brain',
  },
  coding: {
    label: 'Coding',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    icon: 'code',
  },
  math: {
    label: 'Math',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    icon: 'calculator',
  },
  creative: {
    label: 'Creative',
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
    icon: 'sparkles',
  },
  factual: {
    label: 'Factual',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    icon: 'search',
  },
};

// =============================================================================
// Free Tier Types
// =============================================================================

/** Free tier report request */
export interface FreeReportRequest {
  topic: string;
  turnstile_token: string;
  /** Email - optional for 1st fingerprint query, required for 2nd+ */
  email?: string;
  /** Browser fingerprint ID for Sealed Verdict flow */
  fingerprint_id?: string;
  /** Research intensity level (free tier restricted to 'fast') */
  intensity?: ResearchIntensity;
  /** Research mode/blueprint (auto, financial, strategic) */
  mode?: ResearchMode;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

/** Execution mode for tasks */
export type TaskExecutionMode = 'parallel' | 'sequential';

/** Task breakdown for real-time UI feedback */
export interface TaskBreakdown {
  name: string;
  description: string;
  /** Execution mode: parallel (independent) or sequential (depends on previous) */
  execution_mode?: TaskExecutionMode;
  /** Task capability hint for smart routing */
  required_capability?: TaskCapability;
}

/** Free tier report response */
export interface FreeReportResponse {
  success: boolean;
  message: string;
  lead_id?: string;
  report_id?: string;
  reports_remaining?: number;
  is_new_lead?: boolean;
  tasks?: TaskBreakdown[];  // Real-time task breakdown from Sync-Plan
  planning_time_ms?: number;  // How long planning took
  /** True if this is a "sealed verdict" (2nd+ query without email) */
  is_sealed?: boolean;
  /** Effective intensity (may be adjusted based on tier) */
  intensity?: ResearchIntensity;
  /** Credit multiplier display string (e.g., "1x", "5x", "10x+") */
  credit_multiplier?: string;
  /** Research mode (auto, financial, strategic) */
  mode?: ResearchMode;
}

/** Streaming research plan response for authenticated users */
export interface StreamResearchPlanResponse {
  session_id: string;
  tasks: TaskBreakdown[];
  planning_time_ms: number;
  /** Effective intensity (may be adjusted based on tier) */
  intensity: ResearchIntensity;
  /** Credit multiplier display string (e.g., "1x", "5x", "10x+") */
  credit_multiplier: string;
}

/** Fingerprint check request */
export interface FingerprintCheckRequest {
  fingerprint_id: string;
}

/** Fingerprint check response */
export interface FingerprintCheckResponse {
  fingerprint_id: string;
  query_count: number;
  queries_remaining: number;  // Number of free queries remaining before signup required
  max_free_queries: number;   // Maximum free queries allowed per fingerprint
  is_first_query: boolean;
  requires_signup: boolean;   // True if user has exceeded free query limit and must sign up
  signup_credits: number;     // Number of credits user gets on signup (e.g., 150)
  // A/B Test fields
  ab_test_variant: 'sealed_verdict' | 'free_queries';  // Which A/B test variant this user is in
  show_sealed_overlay: boolean;  // True if next query should show sealed plexiglass overlay
}

/** Free tier status response */
export interface FreeReportStatusResponse {
  email: string;
  reports_used: number;
  reports_remaining: number;
  max_reports: number;
  can_request_report: boolean;
}

// Influencer account types
export interface CreateInfluencerRequest {
  email: string;
  first_name?: string;
  last_name?: string;
  days?: number;
}

export interface CreateInfluencerResponse {
  customer_id: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  tier: string;
  active_days: number;
  expires_at: string;
  credits_granted: number;
}

export interface InfluencerAccount {
  customer_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  customer_name: string;
  created_at: string | null;
  last_login: string | null;
  is_active: boolean;
  subscription_status: string | null;
  subscription_start: string | null;
  subscription_end: string | null;
  newsletters_generated: number;
  total_cost: number;
  total_api_calls: number;
  last_activity: string | null;
  effective_status: string;
  // Credit-based analytics
  credits_remaining: number;
  days_remaining: number;
}

export interface InfluencerAccountsSummary {
  total_cost: number;
  total_newsletters: number;
  active_count: number;
  expired_count: number;
  // Credit analytics
  total_credits_remaining: number;
  total_credits_used: number;
}

export interface InfluencerAccountsResponse {
  count: number;
  influencers: InfluencerAccount[];
  summary: InfluencerAccountsSummary;
}

// Twitter Admin types
export interface TwitterPost {
  id: string;
  topic_id: string | null;
  content_type: string;
  tweets: string[];
  tweet_ids: string[];
  status: string;
  posted_at: string | null;
  created_at: string;
  ticker: string | null;
  stance: string | null;
  confidence: number | null;
  pipeline_type: string;
  variant: string;
  likes: number;
  retweets: number;
  replies: number;
  bookmarks: number;
  impressions: number;
  engagement_rate: number;
}

export interface TwitterTopic {
  id: string;
  title: string;
  category: string;
  pipeline_type: string;
  tickers: string[];
  source: string;
  final_score: number;
  status: string;
  discovered_at: string;
}

export interface TwitterDashboardStats {
  posts_today: number;
  posts_this_week: number;
  avg_engagement_rate: number;
  total_impressions_week: number;
  total_likes_week: number;
  total_retweets_week: number;
  top_post: Record<string, unknown> | null;
}

export interface TwitterABTestResult {
  variant: string;
  post_count: number;
  avg_engagement: number;
  avg_impressions: number;
  avg_link_clicks: number;
}

// Re-export research mode types for convenience
export type {
  ResearchMode,
  ResearchModeConfig,
  AgentRole,
  RejectedArgument,
  RejectionReason,
  DataSourceTimestamp,
  ExportFormat,
} from './researchModes';

export { REJECTION_REASON_CONFIG } from './researchModes';
export { RESEARCH_MODE_CONFIGS, getAgentRoleForModel, getModeConfig } from './researchModeConfigs';
